import React from 'react';
import useHasMounted from './useHasMounted';

/**
 * A random number generator for choosing what to render, e.g. with
 * `sampleArray` or `shuffleArray`.
 *
 * Random choices can't match the server-rendered HTML, so on the server and
 * while hydrating it's seeded from `seed` instead. Components that mount
 * later, e.g. on client-side navigation, get `Math.random`.
 *
 * Choose once, in `useMemo` or a `useState` initialiser, so the choice is
 * kept after hydrating.
 */
export default function useRandom(seed = ''): () => number {
    const hasMounted = useHasMounted();
    return React.useMemo(() => {
        if (hasMounted) {
            return Math.random;
        }
        // mulberry32, seeded with a hash of `seed`
        let state = Array.from(seed).reduce(
            (hash, char) => Math.imul(hash ^ char.charCodeAt(0), 2654435761),
            1779033703,
        );
        return (): number => {
            state = (state + 0x6d2b79f5) | 0;
            let t = Math.imul(state ^ (state >>> 15), 1 | state);
            t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
            return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
    }, [hasMounted, seed]);
}
